// OKR / performance rating scale, tone maps, and aggregation helpers.
import { AG } from '../theme/tokens';

export const OKR_RATINGS = ['Exceptional', 'Exceeds', 'Meets', 'Below', 'Unsatisfactory'];
export const RATING_NUM = { Exceptional: 5, Exceeds: 4, Meets: 3, Below: 2, Unsatisfactory: 1 };
export const ratingTone = (r) => ({ Exceptional: 'success', Exceeds: 'success', Meets: 'info', Below: 'warning', Unsatisfactory: 'error' }[r] || 'neutral');
export const okrStatusTone = (s) => ({ Assigned: 'neutral', Accepted: 'info', Submitted: 'warning', Reviewed: 'success', Finalized: 'primary' }[s] || 'neutral');
// Tone for the per-employee roll-up status (HR Assessments / Calibration).
export const rollupStatusTone = (s) => ({ Finalized: 'primary', Reviewed: 'success', 'In review': 'warning', 'In progress': 'info', 'Not started': 'neutral', 'No objectives': 'neutral' }[s] || 'neutral');

// Period options for assigning OKRs — Quarterly | Monthly | Annual.
export const CYCLE_TYPES = ['Quarterly', 'Monthly', 'Annual'];
export function cycleOptionsFor(type) {
  const now = new Date();
  const y = now.getFullYear();
  if (type === 'Annual') return [`FY${y}`, `FY${y + 1}`];
  if (type === 'Monthly') {
    const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(y, now.getMonth() + i, 1);
      return `${m[d.getMonth()]} ${d.getFullYear()}`;
    });
  }
  return [`Q1 ${y}`, `Q2 ${y}`, `Q3 ${y}`, `Q4 ${y}`, `Q1 ${y + 1}`];
}
export const okrTabBtn = (on) => ({ padding: '8px 16px', borderRadius: 12, fontSize: 14, fontWeight: 500, background: on ? AG.primary : '#fff', color: on ? '#fff' : AG.n600, border: `1px solid ${on ? AG.primary : AG.n200}` });
export const okrLbl = { display: 'block', fontSize: 12, fontWeight: 500, color: AG.textSec, marginBottom: 4 };

export function weightedProgress(list) {
  if (!list || !list.length) return 0;
  const tw = list.reduce((a, o) => a + (o.weight || 0), 0);
  if (!tw) return Math.round(list.reduce((a, o) => a + o.progress, 0) / list.length);
  return Math.round(list.reduce((a, o) => a + o.progress * (o.weight || 0), 0) / tw);
}

export function avgRatingLabel(list, field) {
  const vals = list.map((o) => RATING_NUM[o[field]]).filter(Boolean);
  if (!vals.length) return null;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return OKR_RATINGS[Math.max(0, Math.min(4, 5 - Math.round(avg)))];
}

// Aggregate a flat OKR list into one row per employee (for HR assessments).
// Pass `employees` (the full directory) so people with NO objectives still
// appear — otherwise HR can't see who is missing OKRs.
export function okrsByEmployee(okrs, employees) {
  const map = {};
  if (employees) {
    employees.forEach((e) => { map[e.id] = { id: e.id, name: e.name, dept: e.dept, list: [] }; });
  }
  okrs.forEach((o) => {
    if (!map[o.employeeId]) map[o.employeeId] = { id: o.employeeId, name: o.employeeName, dept: o.dept, list: [] };
    map[o.employeeId].list.push(o);
  });
  return Object.values(map).map((e) => {
    const self = avgRatingLabel(e.list, 'selfRating');
    const manager = avgRatingLabel(e.list, 'managerRating');
    const total = e.list.length;
    const fin = e.list.filter((o) => o.status === 'Finalized').length;
    const rev = e.list.filter((o) => o.status === 'Reviewed').length;
    const submitted = e.list.filter((o) => o.status === 'Submitted').length;
    const accepted = e.list.filter((o) => o.status === 'Accepted').length;
    const status = !total ? 'No objectives'
      : fin === total ? 'Finalized'
      : (fin + rev) === total ? 'Reviewed'
      : submitted > 0 ? 'In review'
      : accepted > 0 ? 'In progress'
      : 'Not started';
    const gap = self && manager && Math.abs(RATING_NUM[self] - RATING_NUM[manager]) >= 2;
    return {
      ...e,
      count: total,
      progress: weightedProgress(e.list),
      self, manager, status, gap,
      reviewed: rev + fin,
      finalizable: rev,            // reviewed OKRs HR can still sign off
      totalWeight: e.list.reduce((a, o) => a + (o.weight || 0), 0),
    };
  });
}
